import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, ShieldCheck, ShieldX, Key, RefreshCw } from "lucide-react";
import { useAuthenticatedUserQuery } from "@/api/queries/AuthQueries";
import { TotpSetupForm } from "./TotpSetupForm";
import { BackupCodesDisplay } from "./BackupCodesDisplay";
import { useDisableTotpMutation, useGenerateBackupCodesMutation } from "@/api/mutations/TotpMutations";

interface TwoFactorQuickSetupProps {
    onClose: () => void;
}

export const TwoFactorQuickSetup: React.FC<TwoFactorQuickSetupProps> = ({ onClose }) => {
    const [step, setStep] = useState<"PASSWORD" | "SETUP" | "BACKUP_CODES" | "DISABLE">("PASSWORD");
    const [action, setAction] = useState<"ENABLE" | "DISABLE" | "GENERATE_BACKUP">("ENABLE");
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [newBackupCodes, setNewBackupCodes] = useState<string[]>([]);

    const { data: session, refetch } = useAuthenticatedUserQuery();
    const disableTotpMutation = useDisableTotpMutation(setIsLoading, setError);
    const generateBackupCodesMutation = useGenerateBackupCodesMutation(setIsLoading, setError);

    const isTwoFactorEnabled = session?.user?.twoFactorEnabled;

    // Set initial action based on 2FA status
    React.useEffect(() => {
        if (isTwoFactorEnabled) {
            setAction("DISABLE");
        } else {
            setAction("ENABLE");
        }
    }, [isTwoFactorEnabled]);

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!password.trim()) {
            setError("Please enter your password");
            return;
        }

        setError(null);

        if (action === "ENABLE") {
            // Go to TOTP setup
            setStep("SETUP");
        } else if (action === "DISABLE") {
            // Disable 2FA directly
            disableTotpMutation.mutate(password, {
                onSuccess: () => {
                    refetch();
                    onClose();
                }
            });
        } else if (action === "GENERATE_BACKUP") {
            // Generate backup codes
            generateBackupCodesMutation.mutate(password, {
                onSuccess: (result) => {
                    if (result?.backupCodes) {
                        setNewBackupCodes(result.backupCodes);
                        setStep("BACKUP_CODES");
                    }
                }
            });
        }
    };

    const handleSetupComplete = (backupCodes: string[]) => {
        setNewBackupCodes(backupCodes);
        setStep("BACKUP_CODES");
        refetch();
    };

    const handleBackupCodesComplete = () => {
        onClose();
    };

    const resetToPassword = () => {
        setStep("PASSWORD");
        setPassword("");
        setError(null);
    };

    if (step === "SETUP") {
        return (
            <>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Enable Two-Factor Authentication
                    </DialogTitle>
                </DialogHeader>
                <TotpSetupForm password={password} onSetupComplete={handleSetupComplete} onCancel={resetToPassword} />
            </>
        );
    }

    if (step === "BACKUP_CODES") {
        return (
            <>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5 text-green-600" />
                        {action === "ENABLE" ? "2FA Enabled Successfully!" : "New Backup Codes Generated"}
                    </DialogTitle>
                </DialogHeader>
                <BackupCodesDisplay backupCodes={newBackupCodes} onContinue={handleBackupCodesComplete} />
            </>
        );
    }

    // PASSWORD step
    return (
        <>
            <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                    {isTwoFactorEnabled ? (
                        <ShieldCheck className="h-5 w-5 text-green-600" />
                    ) : (
                        <ShieldX className="h-5 w-5 text-gray-400" />
                    )}
                    Two-Factor Authentication
                </DialogTitle>
                <DialogDescription>
                    {isTwoFactorEnabled
                        ? "Your account is protected with 2FA. Choose an action below."
                        : "Add an extra layer of security to your account."}
                </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Action Selection */}
                {isTwoFactorEnabled && (
                    <div className="space-y-2">
                        <Label>What would you like to do?</Label>
                        <div className="grid gap-2">
                            <Button
                                variant={action === "GENERATE_BACKUP" ? "default" : "outline"}
                                onClick={() => setAction("GENERATE_BACKUP")}
                                className="justify-start"
                            >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Generate New Backup Codes
                            </Button>
                            <Button
                                variant={action === "DISABLE" ? "destructive" : "outline"}
                                onClick={() => setAction("DISABLE")}
                                className="justify-start"
                            >
                                <ShieldX className="mr-2 h-4 w-4" />
                                Disable Two-Factor Authentication
                            </Button>
                        </div>
                    </div>
                )}

                {!isTwoFactorEnabled && (
                    <div className="flex items-center gap-2 rounded-lg bg-blue-50 p-3 dark:bg-blue-950">
                        <Key className="h-5 w-5 text-blue-600" />
                        <span className="text-sm">Enable 2FA to secure your account with an authenticator app</span>
                    </div>
                )}

                {/* Password Form */}
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="password">
                            {action === "ENABLE"
                                ? "Enter your password to set up 2FA"
                                : action === "DISABLE"
                                  ? "Enter your password to disable 2FA"
                                  : "Enter your password to generate new backup codes"}
                        </Label>
                        <Input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter your password"
                            autoFocus
                            autoComplete="current-password"
                        />
                    </div>

                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={isLoading}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isLoading || !password.trim()}
                            variant={action === "DISABLE" ? "destructive" : "default"}
                            className="flex-1"
                        >
                            {isLoading ? (
                                <div className="flex items-center gap-2">
                                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                                    {action === "ENABLE"
                                        ? "Setting up..."
                                        : action === "DISABLE"
                                          ? "Disabling..."
                                          : "Generating..."}
                                </div>
                            ) : action === "ENABLE" ? (
                                "Set up 2FA"
                            ) : action === "DISABLE" ? (
                                "Disable 2FA"
                            ) : (
                                "Generate Codes"
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </>
    );
};
